/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { Query } from 'react-apollo';

import useRouter from 'js/lib/useRouter';
import user from 'js/lib/user';

import type { Theme } from '@coursera/cds-core';
import { Button, Grid, Hidden, Typography } from '@coursera/cds-core';
import { Card } from '@coursera/coursera-ui';

import { isImprovedCertEnrollmentsEnabled } from 'bundles/degree-home/utils/experimentUtils';
import TrackedButton from 'bundles/page/components/TrackedButton';

import bannerBackgroundImage from 'images/degree-home/banner-background.svg';
import calendarImage from 'images/degree-home/calendar.png';

import _t from 'i18n!nls/degree-home';

import { useCoursesContext } from '../CoursesProvider';
import type {
  GetUpcomingCourseStatesQuery_DegreeLearnerCourseStatesV1Resource_get_notStarted_courseProgressState_DegreeLearnerCourseStatesV1_notStartedMember as CourseProgressState,
  GetUpcomingCourseStatesQuery_DegreeLearnerCourseStatesV1Resource_get_notStarted as NotStartedCourse,
} from '../UpcomingCourses/__generated__/getUpcomingCourseStatesQuery';
import EnrollmentReminderBannerDescription from './EnrollmentReminderBannerDescription';
import { getEnrollableSessionsQuery } from './EnrollmentReminderQueries';
import type {
  GetEnrollableSessionsQuery_OnDemandSessionsV1Resource_nextEnrollableSessionsByUserAndCourse_elements as EnrollableSession,
  GetEnrollableSessionsQuery,
  GetEnrollableSessionsQueryVariables,
} from './__generated__/GetEnrollableSessionsQuery';

const styles = {
  contentContainer: (theme: Theme) => css`
    background-image: url(${bannerBackgroundImage});
    background-position: right;
    background-repeat: no-repeat;
    background-size: contain;
    padding: ${theme.spacing(48)} 40px;

    ${theme.breakpoints.down('xs')} {
      background-size: cover;
    }
  `,
  bannerHeading: (theme: Theme) => css`
    margin-bottom: ${theme.spacing(8)};
  `,
  enrollBtn: (theme: Theme) => css`
    margin-top: ${theme.spacing(24)};
  `,
  bottomSpace: css`
    margin-bottom: 1.5rem;
  `,
  imgGrid: css`
    display: flex;
    justify-content: right;
    align-items: center;
  `,
};

// Filters 'notStarted' courses to find items where there is no active session, and there are sessions availble to enroll in
const enrollableCoursesFilterFn = (notStartedCourse: NotStartedCourse) => {
  const progressState = notStartedCourse.courseProgressState as unknown as CourseProgressState;
  return !progressState.notStarted.activeSessionId && progressState.notStarted.hasEnrollableSession;
};

type EnrollmentReminderBannerContentProps = {
  enrollableCourses?: NotStartedCourse[];
  singleEnrollableCourse?: EnrollableSession | null;
};

export const EnrollmentReminderBannerContent: React.VFC<EnrollmentReminderBannerContentProps> = ({
  enrollableCourses,
  singleEnrollableCourse,
}) => {
  const { setShowEnrollmentDialog } = useCoursesContext();
  const router = useRouter();

  const handleEnrollClick = () => {
    router.push({
      ...router.location,
      params: router.params,
      query: {
        ...router.location.query,
        showEnrollmentDialog: true,
      },
    });

    setShowEnrollmentDialog(true);
  };

  if (!enrollableCourses || enrollableCourses.length < 1) return null;

  return (
    <Card
      isCardBlock={true}
      htmlAttributes={{ 'data-e2e': 'enrollment-reminder-banner' }}
      aria-label={_t('Enrollment reminder')}
      style={{ marginBottom: '1.5rem', backgroundColor: '#EBF3FF', boxShadow: 'none' }}
    >
      <Grid container alignContent="center" css={styles.contentContainer}>
        <Grid item xs={12}>
          <Hidden smUp>
            <img src={calendarImage} alt={_t('Calendar icon')} width="74" css={styles.bottomSpace} />
          </Hidden>
        </Grid>
        <Grid item xs={12} sm={9}>
          <Typography variant="h2semibold" color="body" css={styles.bannerHeading}>
            {_t('Jump into your upcoming courses')}
          </Typography>
          <EnrollmentReminderBannerDescription
            enrollableCourses={enrollableCourses}
            singleEnrollableCourse={singleEnrollableCourse}
          />
          <Button
            css={styles.enrollBtn}
            variant="primary"
            size="small"
            onClick={handleEnrollClick}
            component={TrackedButton}
            trackingName="reminder_banner_start_enrollment"
            withVisibilityTracking={false}
            requireFullyVisible={false}
          >
            {_t('Start enrollment')}
          </Button>
        </Grid>
        <Grid item xs={12} sm={3} css={styles.imgGrid}>
          <Hidden xsDown>
            <img src={calendarImage} alt={_t('calendar icon')} width="192" />
          </Hidden>
        </Grid>
      </Grid>
    </Card>
  );
};

type Props = {
  degreeId: string;
};

export const EnrollmentReminderBanner: React.FunctionComponent<Props> = () => {
  const { upcomingCoursesData } = useCoursesContext();
  const enrollableCourses = upcomingCoursesData?.filter(enrollableCoursesFilterFn);

  return (
    <React.Fragment>
      {isImprovedCertEnrollmentsEnabled() && (
        <Query<GetEnrollableSessionsQuery, GetEnrollableSessionsQueryVariables>
          query={getEnrollableSessionsQuery}
          skip={enrollableCourses?.length !== 1}
          variables={{
            courseId: enrollableCourses?.[0]?.id || '',
            userId: user.get().id.toString(),
          }}
        >
          {({ data }) => {
            const singleEnrollableCourse =
              data?.OnDemandSessionsV1Resource?.nextEnrollableSessionsByUserAndCourse?.elements?.[0];

            return (
              <EnrollmentReminderBannerContent
                enrollableCourses={enrollableCourses}
                singleEnrollableCourse={singleEnrollableCourse}
              />
            );
          }}
        </Query>
      )}
    </React.Fragment>
  );
};

export default EnrollmentReminderBanner;
